import { db, auth } from '../../config/firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot,
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore';

/**
 * Base service class for Firebase operations with caching
 */
class BaseService {
  constructor(collectionName) {
    this.collectionName = collectionName;
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes default
    this.listeners = new Map();
    this.isInitialized = false;
  }

  /**
   * Get a cached value if it exists and is not expired
   */
  getCached(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    return null;
  }

  /**
   * Set a value in cache with timestamp
   */
  setCached(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Clear cache for a specific key or all cache
   */
  clearCache(key = null) {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      entries: Array.from(this.cache.entries()).map(([key, value]) => ({
        key,
        age: Date.now() - value.timestamp,
        expired: Date.now() - value.timestamp > this.cacheTimeout
      }))
    };
  }

  /**
   * Handle authentication errors and refresh token if needed
   */
  async handleAuthError(error, operation) {
    console.error(`Auth error in ${operation}:`, error);
    
    // Check if it's a permission denied error
    if (error.code === 'permission-denied' && auth.currentUser) {
      try {
        console.log('Attempting to refresh authentication token...');
        await auth.currentUser.getIdToken(true);
        console.log('Token refreshed successfully');
        return true; // Indicate that token was refreshed
      } catch (refreshError) {
        console.error('Failed to refresh token:', refreshError);
        return false;
      }
    }
    
    return false;
  }

  /**
   * Create a query with common parameters
   */
  createQuery(filters = {}, sortBy = null, limitCount = null) {
    let q = collection(db, this.collectionName);
    
    // Apply filters
    Object.entries(filters).forEach(([field, value]) => {
      if (value !== undefined && value !== null) {
        q = query(q, where(field, '==', value));
      }
    });

    // Apply sorting
    if (sortBy) {
      q = query(q, orderBy(sortBy.field, sortBy.direction || 'asc'));
    }

    // Apply limit
    if (limitCount) {
      q = query(q, limit(limitCount));
    }

    return q;
  }

  /**
   * Get documents with caching
   */
  async getDocuments(filters = {}, sortBy = null, limitCount = null, useCache = true) {
    const cacheKey = `docs_${JSON.stringify({ filters, sortBy, limitCount })}`;
    
    if (useCache) {
      const cached = this.getCached(cacheKey);
      if (cached) {
        return cached;
      }
    }

    try {
      const q = this.createQuery(filters, sortBy, limitCount);
      const snapshot = await getDocs(q);
      const documents = snapshot.docs.map(doc => ({
        key: doc.id,
        ...doc.data()
      }));

      if (useCache) {
        this.setCached(cacheKey, documents);
      }

      return documents;
    } catch (error) {
      // Try to handle auth error and retry once
      const tokenRefreshed = await this.handleAuthError(error, 'getDocuments');
      if (tokenRefreshed) {
        try {
          const q = this.createQuery(filters, sortBy, limitCount);
          const snapshot = await getDocs(q);
          const documents = snapshot.docs.map(doc => ({
            key: doc.id,
            ...doc.data()
          }));

          if (useCache) {
            this.setCached(cacheKey, documents);
          }

          return documents;
        } catch (retryError) {
          console.error(`Error fetching documents from ${this.collectionName} after token refresh:`, retryError);
          throw retryError;
        }
      }
      
      console.error(`Error fetching documents from ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Get a single document by ID with caching
   */
  async getDocument(id, useCache = true) {
    const cacheKey = `doc_${id}`;
    
    if (useCache) {
      const cached = this.getCached(cacheKey);
      if (cached) {
        return cached;
      }
    }

    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const document = {
          key: docSnap.id,
          ...docSnap.data()
        };

        if (useCache) {
          this.setCached(cacheKey, document);
        }

        return document;
      } else {
        return null;
      }
    } catch (error) {
      // Try to handle auth error and retry once
      const tokenRefreshed = await this.handleAuthError(error, 'getDocument');
      if (tokenRefreshed) {
        try {
          const docRef = doc(db, this.collectionName, id);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const document = {
              key: docSnap.id,
              ...docSnap.data()
            };

            if (useCache) {
              this.setCached(cacheKey, document);
            }

            return document;
          } else {
            return null;
          }
        } catch (retryError) {
          console.error(`Error fetching document ${id} from ${this.collectionName} after token refresh:`, retryError);
          throw retryError;
        }
      }
      
      console.error(`Error fetching document ${id} from ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Create a new document
   */
  async createDocument(data) {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...data,
        created: new Date().getTime(),
        modified: new Date().getTime()
      });

      // Clear relevant cache
      this.clearCache();

      return {
        key: docRef.id,
        ...data,
        created: new Date().getTime(),
        modified: new Date().getTime()
      };
    } catch (error) {
      console.error(`Error creating document in ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Update an existing document
   */
  async updateDocument(id, data) {
    try {
      const docRef = doc(db, this.collectionName, id);
      const updateData = {
        ...data,
        modified: new Date().getTime()
      };

      await updateDoc(docRef, updateData);

      // Clear relevant cache
      this.clearCache(`doc_${id}`);
      this.clearCache();

      // Get the updated document to return complete data
      const updatedDoc = await getDoc(docRef);
      if (updatedDoc.exists()) {
        return {
          key: updatedDoc.id,
          ...updatedDoc.data()
        };
      } else {
        throw new Error('Document not found after update');
      }
    } catch (error) {
      console.error(`Error updating document ${id} in ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Delete a document
   */
  async deleteDocument(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);

      // Clear relevant cache
      this.clearCache(`doc_${id}`);
      this.clearCache();

      return true;
    } catch (error) {
      console.error(`Error deleting document ${id} from ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Set up real-time listener for documents
   */
  subscribeToDocuments(filters = {}, callback, options = {}) {
    const { sortBy = null, limitCount = null } = options;
    const cacheKey = `listener_${JSON.stringify({ filters, sortBy, limitCount })}`;

    // Remove existing listener if any
    if (this.listeners.has(cacheKey)) {
      this.listeners.get(cacheKey)();
      this.listeners.delete(cacheKey);
    }

    try {
      const q = this.createQuery(filters, sortBy, limitCount);
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const documents = snapshot.docs.map(doc => ({
          key: doc.id,
          ...doc.data()
        }));

        // Update cache
        this.setCached(`docs_${JSON.stringify({ filters, sortBy, limitCount })}`, documents);

        // Call callback with documents and changes
        const changes = snapshot.docChanges().map(change => ({
          type: change.type,
          doc: {
            key: change.doc.id,
            ...change.doc.data()
          }
        }));

        callback(documents, changes);
      }, (error) => {
        console.error(`Error in ${this.collectionName} listener:`, error);
      });

      this.listeners.set(cacheKey, unsubscribe);
      return unsubscribe;
    } catch (error) {
      console.error(`Error setting up listener for ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Set up real-time listener for a single document
   */
  subscribeToDocument(id, callback) {
    const cacheKey = `listener_doc_${id}`;

    // Remove existing listener if any
    if (this.listeners.has(cacheKey)) {
      this.listeners.get(cacheKey)();
      this.listeners.delete(cacheKey);
    }

    try {
      const docRef = doc(db, this.collectionName, id);
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const document = {
            key: docSnap.id,
            ...docSnap.data()
          };

          // Update cache
          this.setCached(`doc_${id}`, document);

          callback(document, { type: 'modified' });
        } else {
          // Document doesn't exist
          this.clearCache(`doc_${id}`);
          callback(null, { type: 'removed' });
        }
      }, (error) => {
        console.error(`Error in ${this.collectionName} document listener:`, error);
      });

      this.listeners.set(cacheKey, unsubscribe);
      return unsubscribe;
    } catch (error) {
      console.error(`Error setting up document listener for ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Unsubscribe from all listeners
   */
  unsubscribeAll() {
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners.clear();
  }

  /**
   * Batch operations for better performance
   */
  async batchOperation(operations) {
    const { write } = await import('firebase/firestore');
    const batch = write(db);

    try {
      operations.forEach(operation => {
        const { type, id, data } = operation;
        const docRef = doc(db, this.collectionName, id);

        switch (type) {
          case 'create':
            batch.set(docRef, {
              ...data,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            });
            break;
          case 'update':
            batch.update(docRef, {
              ...data,
              updatedAt: new Date().toISOString()
            });
            break;
          case 'delete':
            batch.delete(docRef);
            break;
          default:
            throw new Error(`Unknown operation type: ${type}`);
        }
      });

      await batch.commit();

      // Clear cache after batch operation
      this.clearCache();

      return true;
    } catch (error) {
      console.error(`Error in batch operation for ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Initialize the service
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    // Set up any necessary initialization
    this.isInitialized = true;
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.unsubscribeAll();
    this.clearCache();
    this.isInitialized = false;
  }
}

export default BaseService; 